/**
 * Region-of-Interest calibration — quadrilateral with perspective.
 *
 * # Why this model
 *
 * Anchor-based and pitch-fitting calibrators assume the video shows a
 * recognizable soccer pitch that can be fit to a known geometry. Typical
 * youth footage violates that assumption: shared turf has 3–4 overlaid
 * pitch systems, small-sided games ignore the full-pitch lines, training
 * clips may show only a corner of the field.
 *
 * The ROI model sidesteps the problem. The coach draws a four-corner
 * quadrilateral around the area they care about, and clicks each player +
 * the ball to declare tracking intent. The quad IS the coordinate space —
 * no pitch-geometry assumptions. The quad being a quad (not a rectangle)
 * captures perspective: the near edge is wider than the far edge, so a
 * player standing at grid (0.5, 0.9) is near the camera and a player at
 * (0.5, 0.1) is at the far end.
 *
 * # Ball as scale reference
 *
 * A soccer ball has a known physical diameter by age band (Size 3 = 19 cm,
 * Size 4 = 20.5 cm, Size 5 = 22 cm). Pixel diameter at the ball's position
 * → meters per pixel AT THAT DEPTH. Combined with the perspective quad,
 * downstream tracking and 3D replay can compute rough metric distances
 * across the field.
 *
 * # Downstream compat
 *
 * `intentToVideoCalibration()` emits a standard VideoCalibration from the
 * quad + ball scale. The 4 quad corners map to the 4 pitch-corner anchor
 * ids (n_corner_nw/ne, s_corner_sw/se), so tracker + 3D replay +
 * persistence consume it unchanged.
 */
import { computeHomography, invertHomography, pixelToWorld, type Correspondence, type Homography } from '../homography';
import type { PitchDims } from './pitchAnchors';
import type { CapturedEntity, Keyframe, VideoCalibration } from './types';

// ---------- constants ----------

const YARDS_PER_METER = 1.0936132983;

/** Soccer-ball physical diameters by size. Youth age bands:
 *   • Size 3 — U8 and under. Circumference 58–61 cm → diameter ~19 cm
 *   • Size 4 — U9 to U12.    Circumference 63.5–66 cm → diameter ~20.5 cm
 *   • Size 5 — U13 and up.   Circumference 68–70 cm → diameter ~22 cm
 *  Stored in metres so all metric math stays in SI. */
export const BALL_REAL_DIAMETER_M: Record<3 | 4 | 5, number> = {
  3: 0.19,
  4: 0.205,
  5: 0.22,
};

export type BallSize = 3 | 4 | 5;

/** Pitch size presets (yards). The coach picks one from the ROI selector;
 *  the selected dims become authoritative for 3D replay sizing. Ball-scale
 *  estimation is noisy — presets give a reliable field-vs-player ratio so
 *  the 3D scene doesn't render players bigger than the pitch. */
export interface PitchPreset {
  id: string;
  label: string;
  width: number;  // yards
  length: number; // yards
}

export const PITCH_PRESETS: PitchPreset[] = [
  { id: 'full_11v11',  label: 'Full pitch (11v11)', width: 75, length: 115 },
  { id: '9v9',         label: '9v9',                width: 50, length: 70  },
  { id: '7v7',         label: '7v7',                width: 35, length: 55  },
  { id: '5v5',         label: '5v5 / futsal',       width: 25, length: 40  },
  { id: '4v4',         label: '4v4',                width: 20, length: 30  },
];

export function pitchPresetById(id: string | null | undefined): PitchPreset | null {
  if (!id) return null;
  return PITCH_PRESETS.find((p) => p.id === id) ?? null;
}

// ---------- data model ----------

/**
 * Quadrilateral in video-intrinsic pixel space — 4 corners describing the
 * playing area as the camera sees it (with perspective). Conventions:
 *
 *   near_*  = lower in the frame (y larger), closer to the camera
 *   far_*   = higher in the frame (y smaller), farther from the camera
 *   *_left / *_right = as the camera sees them
 *
 * Corners must form a convex quad. Grid density is UI-facing only — the
 * math uses a continuous homography independent of grid cells.
 */
export interface RegionOfInterest {
  nearLeft:  [number, number];
  nearRight: [number, number];
  farLeft:   [number, number];
  farRight:  [number, number];
  gridCols: number;
  gridRows: number;
  /** Optional pitch preset id (PITCH_PRESETS) or 'custom'. When set,
   *  overrides the ball-scale-derived dims so 3D replay has a reliable
   *  field-vs-player ratio. Null/undefined falls back to ball-derived. */
  pitchPresetId?: string | null;
  /** Explicit pitch dimensions in yards when pitchPresetId === 'custom'.
   *  Ignored when a named preset is selected. */
  customPitch?: { width: number; length: number } | null;
}

/**
 * Pixel-diameter of the ball at the anchor frame + the real-world diameter
 * implied by the age band. Used to derive meters-per-pixel at the ball's
 * depth. The full perspective homography handles the across-field scaling.
 */
export interface BallScaleReference {
  pixelDiameter: number;
  pixelCenter: [number, number];
  ballSize: BallSize;
  realDiameter: number; // meters — set from ballSize via BALL_REAL_DIAMETER_M
}

/**
 * A CapturedEntity extended with the pixel position where the coach first
 * tagged them. The tracker uses this as a seed for frame-to-frame matching.
 */
export interface TrackedEntity extends CapturedEntity {
  initialPixel: [number, number];
}

/**
 * The coach's full tracking intent — what region, what scale, what entities.
 */
export interface CaptureIntent {
  videoName: string;
  roi: RegionOfInterest;
  ballScale: BallScaleReference | null;
  entities: TrackedEntity[];
  anchorFrameT: number;
}

// ---------- quad math ----------

/** Compute the homography mapping the unit square (0..1, 0..1) to the quad's
 *  pixel corners. u=0 is the left edge, u=1 the right. v=0 is the far edge,
 *  v=1 the near edge — this matches the convention that "bottom of the frame
 *  (y large) is closer to the camera". */
export function computeRoiGridHomography(roi: RegionOfInterest): Homography | null {
  const corrs: Correspondence[] = [
    { world: [0, 0], pixel: roi.farLeft   },
    { world: [1, 0], pixel: roi.farRight  },
    { world: [0, 1], pixel: roi.nearLeft  },
    { world: [1, 1], pixel: roi.nearRight },
  ];
  return computeHomography(corrs);
}

/** Convert a point on the unit-square grid to pixel via perspective. */
export function gridToPixel(
  roi: RegionOfInterest,
  u: number,
  v: number,
): [number, number] {
  const H = computeRoiGridHomography(roi);
  if (!H) return roi.nearLeft; // degenerate fallback
  const d = H[6] * u + H[7] * v + H[8];
  if (Math.abs(d) < 1e-10) return roi.nearLeft;
  return [
    (H[0] * u + H[1] * v + H[2]) / d,
    (H[3] * u + H[4] * v + H[5]) / d,
  ];
}

/** Convert a pixel to unit-square grid (u, v) via the inverse homography. */
export function pixelToGrid(
  roi: RegionOfInterest,
  pixel: [number, number],
): [number, number] {
  const H = computeRoiGridHomography(roi);
  if (!H) return [0, 0];
  const Hinv = invertHomography(H);
  if (!Hinv) return [0, 0];
  const [x, y] = pixel;
  const d = Hinv[6] * x + Hinv[7] * y + Hinv[8];
  if (Math.abs(d) < 1e-10) return [0, 0];
  return [
    (Hinv[0] * x + Hinv[1] * y + Hinv[2]) / d,
    (Hinv[3] * x + Hinv[4] * y + Hinv[5]) / d,
  ];
}

// ---------- ball scale ----------

/** Convert a pixel distance AT THE BALL'S DEPTH to meters using the ball
 *  reference. Across-field scaling is handled by the homography — this
 *  function is for local neighbourhoods around the ball. */
export function pixelDistToMeters(
  ball: BallScaleReference,
  pixelDist: number,
): number {
  if (ball.pixelDiameter <= 0) return 0;
  return (pixelDist / ball.pixelDiameter) * ball.realDiameter;
}

/** Inverse of pixelDistToMeters. */
export function metersToPixelDist(
  ball: BallScaleReference,
  meters: number,
): number {
  if (ball.realDiameter <= 0) return 0;
  return (meters / ball.realDiameter) * ball.pixelDiameter;
}

/**
 * Pitch dimensions (yards). Priority:
 *   1. ROI's custom pitch (pitchPresetId === 'custom' + customPitch set)
 *   2. ROI's named preset (pitchPresetId matches PITCH_PRESETS)
 *   3. Ball-scale-derived estimate (fallback)
 *
 * Presets are reliable; ball-scale is noisy (ball diameter estimate drifts
 * with camera angle / motion blur / partial occlusion) and was producing
 * unusably small pitches in practice — 3D replay players ended up bigger
 * than the rendered field. Presets give a coach-controlled, sensible
 * field-vs-player ratio.
 */
export function roiToPitchDims(
  roi: RegionOfInterest,
  ball: BallScaleReference,
): PitchDims {
  // 1. Custom dims — coach entered specific width/length.
  if (roi.pitchPresetId === 'custom' && roi.customPitch) {
    return { width: roi.customPitch.width, length: roi.customPitch.length };
  }
  // 2. Named preset — authoritative when set.
  const preset = pitchPresetById(roi.pitchPresetId);
  if (preset) {
    return { width: preset.width, length: preset.length };
  }
  // 3. Ball-scale estimate (fallback — was causing tiny pitches).
  const mpp = ball.realDiameter / Math.max(1, ball.pixelDiameter);
  const nearEdgePx = Math.hypot(
    roi.nearRight[0] - roi.nearLeft[0],
    roi.nearRight[1] - roi.nearLeft[1],
  );
  const farEdgePx = Math.hypot(
    roi.farRight[0] - roi.farLeft[0],
    roi.farRight[1] - roi.farLeft[1],
  );
  const leftEdgePx = Math.hypot(
    roi.farLeft[0] - roi.nearLeft[0],
    roi.farLeft[1] - roi.nearLeft[1],
  );
  const rightEdgePx = Math.hypot(
    roi.farRight[0] - roi.nearRight[0],
    roi.farRight[1] - roi.nearRight[1],
  );
  const widthPx = (nearEdgePx + farEdgePx) / 2;
  const lengthPx = (leftEdgePx + rightEdgePx) / 2;
  return {
    width:  widthPx * mpp * YARDS_PER_METER,
    length: lengthPx * mpp * YARDS_PER_METER,
  };
}

// ---------- downstream compat ----------

/**
 * Emit a standard VideoCalibration from a CaptureIntent so the existing
 * tracker + 3D replay pipelines can consume it unchanged.
 *
 * The 4 ROI quad corners map to the 4 pitch-corner anchor ids. Because the
 * quad is not a rectangle (perspective), the resulting homography is a
 * genuine projective transform (not a pure affine) — downstream code sees
 * depth-correct projections.
 *
 *   near_left  → s_corner_sw   (near + left = south-west)
 *   near_right → s_corner_se
 *   far_left   → n_corner_nw   (far + left = north-west)
 *   far_right  → n_corner_ne
 *
 * Returns null if ball-scale is missing or the solver can't invert.
 */
export function intentToVideoCalibration(intent: CaptureIntent): VideoCalibration | null {
  if (!intent.ballScale) return null;
  const dims = roiToPitchDims(intent.roi, intent.ballScale);
  const w = dims.width / 2;
  const l = dims.length / 2;

  const { nearLeft, nearRight, farLeft, farRight } = intent.roi;

  // World (yards) — centered at origin. +y = north (far from camera), -y = south.
  const corrs: Correspondence[] = [
    { world: [-w, +l], pixel: farLeft   },  // n_corner_nw
    { world: [+w, +l], pixel: farRight  },  // n_corner_ne
    { world: [-w, -l], pixel: nearLeft  },  // s_corner_sw
    { world: [+w, -l], pixel: nearRight },  // s_corner_se
  ];
  const H = computeHomography(corrs);
  if (!H) return null;

  return {
    videoName: intent.videoName,
    pitchDims: dims,
    correspondences: [
      { anchorId: 'n_corner_nw', pixel: farLeft   },
      { anchorId: 'n_corner_ne', pixel: farRight  },
      { anchorId: 's_corner_sw', pixel: nearLeft  },
      { anchorId: 's_corner_se', pixel: nearRight },
    ],
    homography: H,
    anchorFrameT: intent.anchorFrameT,
    reprojMean: 0, // direct 4-point solve
    reprojMax: 0,
  };
}

// ---------- defaults ----------

/**
 * Default ROI = isoceles trapezoid centered in the frame, far edge narrower
 * than the near edge. This is a rough "most clips look like this" default —
 * coach drags corners to fit.
 */
export function defaultRoiForFrame(width: number, height: number): RegionOfInterest {
  // Near edge — wide band across the lower part of the frame.
  const nearY = height * 0.88;
  const nearLeftX  = width * 0.08;
  const nearRightX = width * 0.92;
  // Far edge — narrower band higher in the frame.
  const farY = height * 0.32;
  const farLeftX  = width * 0.25;
  const farRightX = width * 0.75;
  return {
    nearLeft:  [nearLeftX,  nearY],
    nearRight: [nearRightX, nearY],
    farLeft:   [farLeftX,   farY],
    farRight:  [farRightX,  farY],
    gridCols: 6,
    gridRows: 4,
    // Default to Full 11v11. Coach picks the right preset on the ROI
    // step. Reliable default > ball-scale-guess default.
    pitchPresetId: 'full_11v11',
    customPitch: null,
  };
}

/** Make a BallScaleReference from a clicked pixel center + radius estimate
 *  + a ball size. The radius can come from a coach drag or a default 18px. */
export function makeBallScale(
  pixelCenter: [number, number],
  pixelDiameter: number,
  ballSize: BallSize,
): BallScaleReference {
  return {
    pixelCenter,
    pixelDiameter: Math.max(4, pixelDiameter),
    ballSize,
    realDiameter: BALL_REAL_DIAMETER_M[ballSize],
  };
}

/** Mint a fresh TrackedEntity id. */
let _entitySeq = 0;
export function nextEntityId(): string {
  _entitySeq++;
  return `e${Date.now().toString(36)}_${_entitySeq}`;
}

/**
 * Split a CaptureIntent into the downstream tracker's expected seed shape:
 * (entities without initialPixel) + (one Keyframe per entity at anchorFrameT
 * projected to world yards). The ball becomes its own CapturedEntity +
 * Keyframe too.
 */
export function intentToTrackerSeed(intent: CaptureIntent): {
  calibration: VideoCalibration;
  initialEntities: CapturedEntity[];
  initialKeyframes: Keyframe[];
} | null {
  const cal = intentToVideoCalibration(intent);
  if (!cal || !intent.ballScale) return null;
  const Hinv = invertHomography(cal.homography);
  if (!Hinv) return null;

  const initialEntities: CapturedEntity[] = intent.entities.map((e) => ({
    id: e.id,
    type: e.type,
    label: e.label,
    color: e.color,
    jerseyNumber: e.jerseyNumber,
    tracked: e.tracked,
  }));

  const initialKeyframes: Keyframe[] = [];
  for (const e of intent.entities) {
    const w = pixelToWorld(Hinv, e.initialPixel);
    if (!w) continue;
    initialKeyframes.push({
      entityId: e.id,
      t: intent.anchorFrameT,
      worldX: w[0],
      worldY: w[1],
    });
  }

  const ballId = `ball_${nextEntityId()}`;
  const ballWorld = pixelToWorld(Hinv, intent.ballScale.pixelCenter);
  if (ballWorld) {
    initialEntities.push({
      id: ballId,
      type: 'ball',
      label: 'Ball',
      color: '#FFFFFF',
    });
    initialKeyframes.push({
      entityId: ballId,
      t: intent.anchorFrameT,
      worldX: ballWorld[0],
      worldY: ballWorld[1],
    });
  } else if (typeof console !== 'undefined') {
    // Homography projected the ball pixel outside the pitch plane — we don't
    // silently drop it, the coach needs to know the calibration is off.
    console.warn('[intentToTrackerSeed] ball pixel did not project to world — ball entity dropped', {
      ballPixel: intent.ballScale.pixelCenter,
    });
  }

  return { calibration: cal, initialEntities, initialKeyframes };
}

// ---------- validation ----------

/** Return true if the quad is convex (no crossed edges) in pixel space.
 *  Useful after a drag to verify the coach hasn't twisted the ROI. */
export function isConvexQuad(roi: RegionOfInterest): boolean {
  // Traverse perimeter clockwise: nearLeft → nearRight → farRight → farLeft.
  // All cross products of successive edges must share a sign for convexity.
  const pts = [roi.nearLeft, roi.nearRight, roi.farRight, roi.farLeft];
  let sign = 0;
  for (let i = 0; i < 4; i++) {
    const [ax, ay] = pts[i];
    const [bx, by] = pts[(i + 1) % 4];
    const [cx, cy] = pts[(i + 2) % 4];
    const cross = (bx - ax) * (cy - by) - (by - ay) * (cx - bx);
    if (cross === 0) continue;
    const s = cross > 0 ? 1 : -1;
    if (sign === 0) sign = s;
    else if (sign !== s) return false;
  }
  return true;
}

// ---------- calibration confidence ----------

/**
 * Assess how much we can trust a pixel → pitch mapping before spending vision
 * calls on top of it. A 4-point homography is always an exact solve, so
 * reprojection error is trivially 0 — useless as a signal. The signals we
 * CAN compute from ROI geometry + declared pitch dims + video frame size:
 *
 *  1. Convexity — any twist breaks the projective transform.
 *  2. Corner containment — corners extrapolated off-frame are guesses.
 *  3. Quad coverage — if the coach framed a tiny patch, projected coords
 *     get noisy near the edges (small pixel errors = big world errors).
 *  4. Perspective plausibility — for a camera above the near touchline, the
 *     far edge should be shorter than the near edge. A flipped or flat quad
 *     means the coach mis-labelled near/far.
 *  5. Aspect plausibility — the quad's implied pitch aspect ratio (far-edge
 *     length / near-edge-to-far-edge depth) should be in the ballpark of the
 *     declared pitch aspect (W/L). Big divergence = wrong preset or wrong
 *     ROI shape.
 *
 * Any failed "hard" check → red. Any warning → amber. All clear → green.
 */
export type CalibrationTier = 'green' | 'amber' | 'red';

export interface CalibrationCheck {
  name: string;
  level: 'ok' | 'warn' | 'fail';
  detail: string;
}

export interface CalibrationQuality {
  tier: CalibrationTier;
  checks: CalibrationCheck[];
  summary: string;
}

function quadAreaPx(roi: RegionOfInterest): number {
  // Shoelace over the four corners in traversal order.
  const pts = [roi.nearLeft, roi.nearRight, roi.farRight, roi.farLeft];
  let sum = 0;
  for (let i = 0; i < 4; i++) {
    const [ax, ay] = pts[i];
    const [bx, by] = pts[(i + 1) % 4];
    sum += ax * by - bx * ay;
  }
  return Math.abs(sum) / 2;
}

function edgeLen(a: [number, number], b: [number, number]): number {
  return Math.hypot(b[0] - a[0], b[1] - a[1]);
}

export function assessCalibration(
  roi: RegionOfInterest,
  pitchDims: PitchDims,
  videoW: number,
  videoH: number,
): CalibrationQuality {
  const checks: CalibrationCheck[] = [];
  let hasFail = false;
  let hasWarn = false;

  // 1. Convexity — hard fail.
  const convex = isConvexQuad(roi);
  checks.push({
    name: 'Quad is convex',
    level: convex ? 'ok' : 'fail',
    detail: convex
      ? 'No twisted edges.'
      : 'The quad crosses itself — homography will produce nonsense. Drag a corner to fix.',
  });
  if (!convex) hasFail = true;

  // 2. Corner containment — hard fail if far off-frame, warn if slightly.
  const pts = [roi.nearLeft, roi.nearRight, roi.farRight, roi.farLeft];
  let worstOverflowPct = 0;
  for (let i = 0; i < 4; i++) {
    const [x, y] = pts[i];
    const overflowX = Math.max(0, -x, x - videoW);
    const overflowY = Math.max(0, -y, y - videoH);
    const overflowPct = Math.max(overflowX / videoW, overflowY / videoH) * 100;
    if (overflowPct > worstOverflowPct) worstOverflowPct = overflowPct;
  }
  if (worstOverflowPct === 0) {
    checks.push({
      name: 'Corners in frame',
      level: 'ok',
      detail: 'All four pitch corners are visible in the clip.',
    });
  } else if (worstOverflowPct < 10) {
    checks.push({
      name: 'Corners in frame',
      level: 'warn',
      detail: `A corner sits up to ${worstOverflowPct.toFixed(0)}% outside the frame — mild extrapolation, usually okay.`,
    });
    hasWarn = true;
  } else {
    checks.push({
      name: 'Corners in frame',
      level: 'fail',
      detail: `A corner sits ${worstOverflowPct.toFixed(0)}% outside the frame — projection at that end is a guess. Zoom out or pick a clip with all corners visible.`,
    });
    hasFail = true;
  }

  // 3. Quad coverage — want at least ~15% of the frame.
  const coveragePct = (quadAreaPx(roi) / Math.max(1, videoW * videoH)) * 100;
  if (coveragePct >= 15) {
    checks.push({
      name: 'Quad coverage',
      level: 'ok',
      detail: `Quad covers ${coveragePct.toFixed(0)}% of the frame — plenty of pixel resolution per yard.`,
    });
  } else if (coveragePct >= 5) {
    checks.push({
      name: 'Quad coverage',
      level: 'warn',
      detail: `Quad covers only ${coveragePct.toFixed(0)}% of the frame. Small pixel nudges turn into big world errors — nudge the corners wider if you can.`,
    });
    hasWarn = true;
  } else {
    checks.push({
      name: 'Quad coverage',
      level: 'fail',
      detail: `Quad covers only ${coveragePct.toFixed(0)}% of the frame. World coords will be very noisy.`,
    });
    hasFail = true;
  }

  // 4. Perspective plausibility — near edge should be longer than far edge
  //    for a standard elevated-sideline angle. Equal or inverted implies
  //    the coach labelled near/far backwards or the quad is flat (top-down
  //    clip, which we can still handle but the warning flags the ambiguity).
  const nearEdge = edgeLen(roi.nearLeft, roi.nearRight);
  const farEdge = edgeLen(roi.farLeft, roi.farRight);
  const ratio = nearEdge / Math.max(1, farEdge);
  if (ratio >= 1.1) {
    checks.push({
      name: 'Perspective',
      level: 'ok',
      detail: `Near edge is ${ratio.toFixed(1)}× the far edge — consistent with a raised sideline camera.`,
    });
  } else if (ratio >= 0.9 && ratio < 1.1) {
    checks.push({
      name: 'Perspective',
      level: 'warn',
      detail: 'Near and far edges look roughly equal length. If this is a top-down or drone clip, fine — otherwise the near/far labels may be flipped.',
    });
    hasWarn = true;
  } else {
    checks.push({
      name: 'Perspective',
      level: 'fail',
      detail: `Far edge is longer than the near edge (${(1 / ratio).toFixed(1)}×). Near and far are labelled backwards — drag the rows to swap, or the 3D scene will be inside-out.`,
    });
    hasFail = true;
  }

  // 5. Aspect plausibility — compare the quad's implied depth:width ratio
  //    to the declared pitch preset. We measure the average of near+far
  //    edges as the quad's "width" and the centre-to-centre distance as
  //    its "length". Both are in pixels, but the ratio is dimensionless.
  const nearCx = (roi.nearLeft[0] + roi.nearRight[0]) / 2;
  const nearCy = (roi.nearLeft[1] + roi.nearRight[1]) / 2;
  const farCx = (roi.farLeft[0] + roi.farRight[0]) / 2;
  const farCy = (roi.farLeft[1] + roi.farRight[1]) / 2;
  const quadLenPx = Math.hypot(farCx - nearCx, farCy - nearCy);
  const quadWidthPx = (nearEdge + farEdge) / 2;
  const quadAspect = quadLenPx / Math.max(1, quadWidthPx); // length / width
  const pitchAspect = pitchDims.length / Math.max(1, pitchDims.width);
  const aspectRatioErr = Math.abs(Math.log(quadAspect / pitchAspect));
  if (aspectRatioErr < 0.4) {
    // ~exp(0.4) ≈ 1.5× tolerance on the ratio — normal perspective slack.
    checks.push({
      name: 'Aspect vs pitch preset',
      level: 'ok',
      detail: `Quad proportions match the declared ${pitchDims.width.toFixed(0)}×${pitchDims.length.toFixed(0)} yd pitch.`,
    });
  } else if (aspectRatioErr < 0.8) {
    checks.push({
      name: 'Aspect vs pitch preset',
      level: 'warn',
      detail: `Quad proportions are off by ${Math.exp(aspectRatioErr).toFixed(1)}× from the declared pitch aspect. Check you picked the right pitch size.`,
    });
    hasWarn = true;
  } else {
    checks.push({
      name: 'Aspect vs pitch preset',
      level: 'fail',
      detail: `Quad proportions are ${Math.exp(aspectRatioErr).toFixed(1)}× off the declared ${pitchDims.width.toFixed(0)}×${pitchDims.length.toFixed(0)} yd pitch. Either the pitch size is wrong or the quad is mis-shaped — fix before running AI capture.`,
    });
    hasFail = true;
  }

  const tier: CalibrationTier = hasFail ? 'red' : hasWarn ? 'amber' : 'green';
  const summary =
    tier === 'green' ? 'Calibration looks solid.' :
    tier === 'amber' ? 'Calibration usable but has warnings.' :
                       'Calibration is risky — AI tracking will likely be wrong.';
  return { tier, checks, summary };
}
